import { memo } from 'react';

/**
 * Sky-MNF half-spaces overlay for the 2D tactics board.
 *
 * Divides the pitch into five vertical channels (Sky-MNF / FM-style
 * tactical convention):
 *   - LW             — left wing
 *   - left half-space
 *   - centre
 *   - right half-space
 *   - RW             — right wing
 *
 * Renders as four thin dashed yellow dividers between the channels
 * spanning the full pitch length. No fills — the dividers alone read
 * as channel boundaries and coaches reference them when explaining
 * "attack down the half-space" / "switch to the wing." Filling the
 * channels would compete with team-territory overlays like Voronoi.
 *
 * Coordinate convention: the 2D pitch SVG uses the landscape mapping
 *   SVG-x = -world.z
 *   SVG-y =  world.x
 * Each divider is a vertical line in WORLD x (constant x, runs from
 * z = -halfL to +halfL). After the axis swap that becomes a HORIZONTAL
 * line in SVG (constant SVG-y, x running from +halfL to -halfL).
 */

const FIELD_LENGTH_M = 105;
const FIELD_WIDTH_M = 68;
const HALF_W = FIELD_WIDTH_M / 2;
const HALF_L = FIELD_LENGTH_M / 2;
const CHANNEL_WIDTH_M = FIELD_WIDTH_M / 5;

/** World-x positions of the four channel dividers — equally spaced
 *  across the pitch width. */
const DIVIDER_XS = [
  -HALF_W + CHANNEL_WIDTH_M,         // -20.4
  -HALF_W + CHANNEL_WIDTH_M * 2,     // -6.8
  -HALF_W + CHANNEL_WIDTH_M * 3,     //  6.8
  -HALF_W + CHANNEL_WIDTH_M * 4,     //  20.4
];

const STROKE_HEX = '#fbbf24';

export interface HalfSpacesLayer2DProps {
  /** Opacity in [0, 1]. */
  opacity: number;
}

function HalfSpacesLayer2DInner({ opacity }: HalfSpacesLayer2DProps) {
  const stroke = `rgba(251, 191, 36, ${opacity})`;
  return (
    <g
      className="t2d-halfspaces-layer"
      data-testid="t2d-halfspaces-layer"
      pointerEvents="none"
      clipPath="url(#t2d-pitch-clip)"
    >
      {DIVIDER_XS.map((worldX, idx) => {
        // World vertical line at constant x, z ∈ [-halfL, halfL]
        // → SVG horizontal segment at y=worldX, x ∈ [-halfL, halfL].
        return (
          <line
            key={idx}
            x1={-HALF_L}
            y1={worldX}
            x2={HALF_L}
            y2={worldX}
            stroke={stroke}
            strokeWidth={0.18}
            strokeDasharray="1.4 1.0"
            strokeLinecap="round"
            data-testid={`t2d-halfspaces-divider-${idx}`}
          />
        );
      })}
    </g>
  );
}

export const HalfSpacesLayer2D = memo(HalfSpacesLayer2DInner);

/** Re-export the geometry so the 3D layer reads the same divider
 *  positions without duplicating the math. */
export const HALFSPACES_DIVIDER_XS = DIVIDER_XS;
export const HALFSPACES_STROKE_HEX = STROKE_HEX;
